/**
 * secret-masker.ts — Client-side & Gateway Secret Redaction
 *
 * Tự động phát hiện và che giấu các thông tin nhạy cảm (API Keys, Tokens, Passwords, DB URLs)
 * trước khi gửi ra ngoài tới các mô hình AI hoặc lưu trữ đám mây.
 * Đảm bảo 100% Sovereign Privacy cho người dùng.
 */

export interface MaskResult {
  maskedText: string;
  hasRedacted: boolean;
  redactedCount: number;
}

const SECRET_PATTERNS: Array<{ name: string; regex: RegExp }> = [
  // Private Keys (PEM format)
  {
    name: 'PRIVATE_KEY',
    regex: /-----BEGIN [A-Z0-9 ]+ PRIVATE KEY-----[\s\S]*?-----END [A-Z0-9 ]+ PRIVATE KEY-----/g,
  },
  // Database Connection Strings with passwords
  {
    name: 'DATABASE_URL',
    regex: /(?:postgres|postgresql|mysql|mongodb(?:\+srv)?|redis|mssql):\/\/[^\s"'`<>]+:[^\s"'`<>@]+@[^\s"'`<>]+/gi,
  },
  // OpenAI / Anthropic / AI API Keys
  {
    name: 'AI_API_KEY',
    regex: /\b(?:sk-[a-zA-Z0-9_\-]{20,}|sk-ant-[a-zA-Z0-9_\-]{20,}|xai-[a-zA-Z0-9_\-]{20,}|AIza[0-9A-Za-z\-_]{35})\b/g,
  },
  // GitHub / GitLab / Cloudflare Tokens
  {
    name: 'ACCESS_TOKEN',
    regex: /\b(?:ghp_[a-zA-Z0-9]{30,}|gho_[a-zA-Z0-9]{30,}|github_pat_[a-zA-Z0-9_]{30,}|glpat-[a-zA-Z0-9_\-]{20,})\b/g,
  },
  // JWT Tokens
  {
    name: 'JWT_TOKEN',
    regex: /\beyJ[a-zA-Z0-9_\-]{10,}\.eyJ[a-zA-Z0-9_\-]{10,}\.[a-zA-Z0-9_\-]{10,}\b/g,
  },
  // Generic Secret assignments in ENV / config files
  {
    name: 'ASSIGNED_SECRET',
    regex: /(?<=\b(?:API_KEY|SECRET|PASSWORD|PASSWD|ACCESS_TOKEN|PRIVATE_KEY|AUTH_TOKEN|SECRET_KEY)\s*[:=]\s*["']?)[a-zA-Z0-9_\-.~+/=@!#$%^&*]{8,}(?=["']?)/gi,
  },
];

/**
 * Che giấu toàn bộ secrets trong văn bản hoặc prompt
 */
export function maskSecrets(input: string): MaskResult {
  if (!input) return { maskedText: input, hasRedacted: false, redactedCount: 0 };

  let masked = input;
  let redactedCount = 0;

  for (const { name, regex } of SECRET_PATTERNS) {
    masked = masked.replace(regex, (match) => {
      redactedCount++;
      return `[REDACTED_${name}]`;
    });
  }

  return {
    maskedText: masked,
    hasRedacted: redactedCount > 0,
    redactedCount,
  };
}
